<!-- Standalone page: its own shell. Embedded in a chapter workspace: just the content, so the sidebar stays. -->
<ng-container *ngIf="!embedded; else financeBody">
<div class="section padding-top-120 padding-top-mob-nav section-background-rana rana-shell-flush">
<div class="section-1400 rana-full-bleed">
<div class="container-fluid px-0">
<div class="section border-4 p-3 p-xl-4 rana-shell-main-inner">
    <ng-container *ngTemplateOutlet="financeBody"></ng-container>
</div>
</div>
</div>
</div>
</ng-container>

<ng-template #financeBody>

    <div class="rana-page-header">
        <div>
            <h1 class="rana-page-title">Finance</h1>
            <p class="rana-page-subtitle mb-0">
                Card payments from Stripe: what came in, what Stripe kept, what was refunded, and what reached the bank.
                <span *ngIf="!canManage"> You are viewing your chapter's payments only.</span>
            </p>
        </div>
    </div>

    <div *ngIf="canManage" class="form-row align-items-end mt-3">
        <div class="col-auto">
            <label class="mb-0 small">Chapter</label>
            <select class="form-control form-control-sm" [(ngModel)]="chapterId" (ngModelChange)="onChapterChange()">
                <option value="">All chapters</option>
                <option *ngFor="let c of chapterOptions" [value]="c.key">{{ c.label }}</option>
            </select>
        </div>
    </div>

    <div class="fin-tabs mt-3">
        <button type="button" class="btn btn-sm" [ngClass]="tab === 'overview' ? 'btn-primary' : 'btn-outline-secondary'" (click)="setTab('overview')">Overview</button>
        <button type="button" class="btn btn-sm" [ngClass]="tab === 'payments' ? 'btn-primary' : 'btn-outline-secondary'" (click)="setTab('payments')">Payments</button>
        <button type="button" class="btn btn-sm" [ngClass]="tab === 'reconciliation' ? 'btn-primary' : 'btn-outline-secondary'" (click)="setTab('reconciliation')">
            Needs attention <span *ngIf="exceptions.length" class="rana-badge rana-badge-warning ml-1">{{ exceptions.length }}</span>
        </button>
        <button type="button" *ngIf="canManage" class="btn btn-sm" [ngClass]="tab === 'payouts' ? 'btn-primary' : 'btn-outline-secondary'" (click)="setTab('payouts')">Payouts to bank</button>
        <button type="button" *ngIf="canManage" class="btn btn-sm" [ngClass]="tab === 'accounts' ? 'btn-primary' : 'btn-outline-secondary'" (click)="setTab('accounts')">Stripe accounts</button>
    </div>

    <!-- ============================ OVERVIEW ============================ -->
    <div *ngIf="tab === 'overview'" class="mt-3">
        <div class="form-row align-items-end mb-3">
            <div class="col-auto"><label class="mb-0 small">From</label><input type="date" class="form-control form-control-sm" [(ngModel)]="from"></div>
            <div class="col-auto"><label class="mb-0 small">To</label><input type="date" class="form-control form-control-sm" [(ngModel)]="to"></div>
            <div class="col-auto"><button type="button" class="btn btn-sm btn-primary" (click)="loadSummary()">Apply</button></div>
        </div>

        <div *ngIf="summary" class="row">
            <div class="col-6 col-lg-3 mb-3"><div class="fin-kpi"><div class="label">Collected</div><div class="value">{{ money(summary.gross) }}</div><div class="hint">{{ summary.paymentCount }} payments (incl. card fee paid by payer)</div></div></div>
            <div class="col-6 col-lg-3 mb-3"><div class="fin-kpi"><div class="label">Stripe fees</div><div class="value">{{ money(summary.fees) }}</div><div class="hint">kept by Stripe</div></div></div>
            <div class="col-6 col-lg-3 mb-3"><div class="fin-kpi"><div class="label">Refunded</div><div class="value">{{ money(summary.refunded) }}</div><div class="hint">returned to payers</div></div></div>
            <div class="col-6 col-lg-3 mb-3"><div class="fin-kpi"><div class="label">Net received</div><div class="value">{{ money(summary.net) }}</div><div class="hint">collected − fees − refunds</div></div></div>
            <div class="col-6 col-lg-3 mb-3"><div class="fin-kpi"><div class="label">Paid out to bank</div><div class="value">{{ money(summary.paidOut) }}</div><div class="hint">arrived in the bank account</div></div></div>
            <div class="col-6 col-lg-3 mb-3"><div class="fin-kpi"><div class="label">Still in Stripe</div><div class="value">{{ money(summary.awaitingPayout) }}</div><div class="hint">not yet paid out</div></div></div>
            <div class="col-12 col-lg-6 mb-3">
                <div class="fin-kpi" style="cursor:pointer" (click)="setTab('reconciliation')">
                    <div class="label">Needs attention</div>
                    <div class="value" [class.text-danger]="attentionTotal > 0">{{ attentionTotal }}</div>
                    <div class="hint">stuck, unlinked, disputed or refunded payments to review</div>
                </div>
            </div>
        </div>

        <div *ngIf="summary" class="fin-identity mb-4 small">
            <strong>How the numbers fit:</strong>
            {{ money(summary.gross) }} collected − {{ money(summary.fees) }} Stripe fees − {{ money(summary.refunded) }} refunded
            = <strong>{{ money(summary.net) }}</strong> net =
            {{ money(summary.paidOut) }} paid out to the bank + {{ money(summary.awaitingPayout) }} still in Stripe.
            <span *ngIf="canManage"> Use “Payouts to bank” to match payments to bank payouts.</span>
        </div>

        <div *ngIf="canManage" class="mb-4">
            <h5>Live Stripe balances</h5>
            <button type="button" class="btn btn-sm btn-outline-secondary" (click)="loadBalances()" [disabled]="balancesLoading">
                {{ balancesLoading ? 'Reading Stripe…' : (balances ? 'Refresh balances' : 'Load balances from Stripe') }}
            </button>
            <div class="table-responsive mt-2 rana-table-flat" *ngIf="balances">
                <table class="table rana-table fin-table">
                    <thead><tr><th>Account</th><th>Used by</th><th class="text-right">Available</th><th class="text-right">Pending</th></tr></thead>
                    <tbody>
                    <tr *ngFor="let b of balances">
                        <td class="fin-mono">{{ b.accountRef }}</td>
                        <td class="small">{{ b.usedBy.slice(0, 3).join(', ') }}<span *ngIf="b.usedBy.length > 3"> +{{ b.usedBy.length - 3 }} more</span></td>
                        <td class="text-right"><span *ngIf="b.error" class="text-danger small">{{ b.error }}</span><span *ngIf="!b.error">{{ money(b.available) }}</span></td>
                        <td class="text-right"><span *ngIf="!b.error">{{ money(b.pending) }}</span></td>
                    </tr>
                    </tbody>
                </table>
            </div>
        </div>

        <div class="row" *ngIf="summary">
            <div class="col-lg-6 mb-4">
                <h5>By type</h5>
                <div *ngFor="let b of summary.byPurpose" class="mb-2">
                    <div class="d-flex justify-content-between small"><span>{{ purposeLabel(b.key) }} <span class="text-muted">({{ b.count }})</span></span><strong>{{ money(b.gross) }}</strong></div>
                    <div class="fin-bar-track"><div class="fin-bar" [style.width.%]="barWidth(b, summary.byPurpose)"></div></div>
                </div>
                <p *ngIf="!summary.byPurpose.length" class="text-muted small">No payments in this period.</p>
            </div>
            <div class="col-lg-6 mb-4">
                <h5>By month</h5>
                <div *ngFor="let b of summary.byMonth" class="mb-2">
                    <div class="d-flex justify-content-between small"><span>{{ b.label }} <span class="text-muted">({{ b.count }})</span></span><strong>{{ money(b.gross) }}</strong></div>
                    <div class="fin-bar-track"><div class="fin-bar" [style.width.%]="barWidth(b, summary.byMonth)"></div></div>
                </div>
            </div>
            <div class="col-lg-6 mb-4">
                <h5>By chapter</h5>
                <table class="table rana-table fin-table">
                    <thead><tr><th>Chapter</th><th class="text-right">Payments</th><th class="text-right">Collected</th><th class="text-right">Fees</th><th class="text-right">Refunded</th><th class="text-right">Net</th></tr></thead>
                    <tbody>
                    <tr *ngFor="let b of summary.byChapter">
                        <td>{{ b.label }}</td><td class="text-right">{{ b.count }}</td><td class="text-right">{{ money(b.gross) }}</td>
                        <td class="text-right">{{ money(b.fees) }}</td><td class="text-right">{{ money(b.refunded) }}</td><td class="text-right">{{ money(b.net) }}</td>
                    </tr>
                    </tbody>
                </table>
            </div>
            <div class="col-lg-6 mb-4" *ngIf="canManage">
                <h5>By Stripe account</h5>
                <table class="table rana-table fin-table">
                    <thead><tr><th>Account</th><th class="text-right">Payments</th><th class="text-right">Collected</th><th class="text-right">Net</th></tr></thead>
                    <tbody>
                    <tr *ngFor="let b of summary.byAccount">
                        <td class="fin-mono">{{ b.label }}</td><td class="text-right">{{ b.count }}</td><td class="text-right">{{ money(b.gross) }}</td><td class="text-right">{{ money(b.net) }}</td>
                    </tr>
                    </tbody>
                </table>
            </div>
        </div>
    </div>

    <!-- ============================ PAYMENTS ============================ -->
    <div *ngIf="tab === 'payments'" class="mt-3">
        <div class="form-row align-items-end mb-3">
            <div class="col-md-2 mb-2"><label class="mb-0 small">Status</label>
                <select class="form-control form-control-sm" [(ngModel)]="filters.status">
                    <option value="">All</option>
                    <option *ngFor="let s of statuses" [value]="s">{{ s }}</option>
                </select></div>
            <div class="col-md-2 mb-2"><label class="mb-0 small">Type</label>
                <select class="form-control form-control-sm" [(ngModel)]="filters.purpose">
                    <option value="">All</option>
                    <option *ngFor="let p of purposes" [value]="p.value">{{ p.label }}</option>
                </select></div>
            <div class="col-md-2 mb-2"><label class="mb-0 small">From</label><input type="date" class="form-control form-control-sm" [(ngModel)]="filters.from"></div>
            <div class="col-md-2 mb-2"><label class="mb-0 small">To</label><input type="date" class="form-control form-control-sm" [(ngModel)]="filters.to"></div>
            <div class="col-md-2 mb-2"><label class="mb-0 small">Search</label><input type="text" class="form-control form-control-sm" placeholder="email, event, id…" [(ngModel)]="filters.q" (keyup.enter)="loadPayments()"></div>
            <div class="col-md-2 mb-2 d-flex">
                <button type="button" class="btn btn-sm btn-primary mr-2" (click)="loadPayments()">Filter</button>
                <button type="button" class="btn btn-sm btn-outline-secondary" (click)="exportPayments()" [disabled]="!payments.length">CSV</button>
            </div>
        </div>

        <div class="table-responsive rana-table-flat" *ngIf="paymentsDtShow">
            <table datatable [dtOptions]="paymentsDt" class="table rana-table fin-table"></table>
        </div>

        <div *ngIf="selected" class="fin-detail mt-3">
            <div class="d-flex justify-content-between flex-wrap">
                <h5 class="mb-2">{{ purposeLabel(selected.purpose) }}<span *ngIf="selected.eventTitle"> · {{ selected.eventTitle }}</span></h5>
                <span class="rana-badge" [ngClass]="statusClass(selected.paymentStatus)">{{ selected.paymentStatus }}</span>
            </div>
            <div class="row">
                <dl class="col-md-4 mb-0">
                    <dt>Payer</dt><dd>{{ selected.customerEmail || selected.userId || '—' }}</dd>
                    <dt>Charged</dt><dd>{{ money(selected.amount) }} <span class="text-muted small" *ngIf="selected.surchargeAmount">(includes {{ money(selected.surchargeAmount) }} card fee)</span></dd>
                    <dt>Stripe fee / Net</dt><dd>{{ isPaid(selected) ? money(selected.stripeFee) + ' / ' + money(net(selected)) : '—' }}</dd>
                    <dt>Refunded</dt><dd>{{ money(selected.amountRefunded) }}</dd>
                </dl>
                <dl class="col-md-4 mb-0">
                    <dt>Created</dt><dd>{{ selected.createdEpoch ? (selected.createdEpoch | date:'medium') : selected.createdDate }}</dd>
                    <dt>Last checked with Stripe</dt><dd>{{ selected.lastSyncedAt ? (selected.lastSyncedAt | date:'medium') : 'never' }} <span class="text-muted small" *ngIf="selected.source">({{ selected.source }})</span></dd>
                    <dt>Registration / membership updated</dt><dd>{{ selected.effectAppliedAt ? 'Yes' : (isPaid(selected) ? 'Not yet' : '—') }}</dd>
                    <dt>Payout</dt><dd>{{ selected.payoutId ? (selected.payoutId + ' · ' + selected.payoutStatus) : 'Not paid out yet' }}</dd>
                </dl>
                <dl class="col-md-4 mb-0">
                    <dt>Stripe account</dt><dd class="fin-mono">{{ selected.stripeAccountRef || 'PLATFORM' }}</dd>
                    <dt>PaymentIntent</dt><dd class="fin-mono">{{ selected.paymentIntentId || '—' }}</dd>
                    <dt>Checkout session</dt><dd class="fin-mono">{{ selected.stripeSessionId || '—' }}</dd>
                    <dt *ngIf="selected.failureReason">Note</dt><dd *ngIf="selected.failureReason" class="text-danger small">{{ selected.failureReason }}</dd>
                </dl>
            </div>

            <div *ngIf="selected.refunds?.length" class="mt-2">
                <strong class="small">Refunds</strong>
                <ul class="small mb-2">
                    <li *ngFor="let r of selected.refunds">{{ money(r.amount) }} — {{ r.reason }}
                        <span class="text-muted">({{ r.source === 'STRIPE_DASHBOARD' ? 'from the Stripe dashboard' : 'by NEC' }}{{ r.createdDate ? ', ' + (r.createdDate | date:'MMM d, y') : '' }})</span></li>
                </ul>
            </div>

            <div class="mt-2">
                <button type="button" class="btn btn-sm btn-outline-secondary mr-2" (click)="refreshFromStripe(selected)">Refresh from Stripe</button>
                <a *ngIf="stripeLink(selected)" class="btn btn-sm btn-outline-secondary mr-2" [href]="stripeLink(selected)" target="_blank" rel="noopener">Open in Stripe</a>
                <button type="button" *ngIf="canRefund(selected)" class="btn btn-sm btn-danger" (click)="openRefund(selected)">Refund…</button>
            </div>

            <div *ngIf="refundOpen && canRefund(selected)" class="mt-3 p-3" style="background:#fff6f4;border:1px solid #f0c9c2;border-radius:8px">
                <h6>Refund to payer</h6>
                <div class="form-row">
                    <div class="col-md-3 mb-2">
                        <label class="small mb-0">Amount (USD) — up to {{ money(refundable(selected)) }}</label>
                        <input type="number" min="0.01" step="0.01" class="form-control form-control-sm" [(ngModel)]="refundAmount">
                    </div>
                    <div class="col-md-9 mb-2">
                        <label class="small mb-0">Reason (kept on the payment and emailed context)</label>
                        <input type="text" class="form-control form-control-sm" [(ngModel)]="refundReason" placeholder="e.g. Event cancelled / duplicate payment">
                    </div>
                </div>
                <p class="small text-muted mb-2">Stripe does not give back its processing fee on refunds. The payer is emailed automatically.
                    The registration is flagged for review, not cancelled.</p>
                <button type="button" class="btn btn-sm btn-danger mr-2" [disabled]="refundBusy" (click)="submitRefund()">{{ refundBusy ? 'Refunding…' : 'Issue refund' }}</button>
                <button type="button" class="btn btn-sm btn-outline-secondary" (click)="refundOpen = false">Cancel</button>
            </div>
        </div>
    </div>

    <!-- ============================ NEEDS ATTENTION ============================ -->
    <div *ngIf="tab === 'reconciliation'" class="mt-3">
        <div *ngIf="canManage" class="mb-3 d-flex flex-wrap align-items-center">
            <button type="button" class="btn btn-sm btn-primary mr-2 mb-2" (click)="runSweep()" [disabled]="sweeping">{{ sweeping ? 'Checking Stripe…' : 'Check open payments now' }}</button>
            <button type="button" class="btn btn-sm btn-outline-secondary mb-2" (click)="startImport()" [disabled]="importing">{{ importing ? 'Importing…' : 'Import last 12 months from Stripe' }}</button>
            <span class="small text-muted ml-3 mb-2">{{ importLog }}</span>
        </div>

        <p *ngIf="!exceptions.length" class="text-muted">Nothing needs attention — every payment is accounted for.</p>

        <div class="table-responsive rana-table-flat" *ngIf="exceptionsDtShow && exceptions.length">
            <table datatable [dtOptions]="exceptionsDt" class="table rana-table fin-table"></table>
        </div>

        <div *ngIf="linkTarget" class="fin-detail mt-3">
            <h6>Link this payment ({{ money(linkTarget.amount) }}, {{ linkTarget.customerEmail || 'unknown payer' }})</h6>
            <p class="small text-muted">Pick what it paid for. Linking confirms that registration or membership as paid.</p>
            <div class="form-row">
                <div class="col-md-3 mb-2"><label class="small mb-0">Paid for</label>
                    <select class="form-control form-control-sm" [(ngModel)]="link.purpose">
                        <option value="EVENT_LOCAL">Event registration</option>
                        <option value="EVENT_BOOKING">Conference booking</option>
                        <option value="MEMBERSHIP">Membership</option>
                        <option value="BIZ_CONFERENCE">Business conference</option>
                    </select></div>
                <ng-container *ngIf="link.purpose === 'EVENT_LOCAL' || link.purpose === 'EVENT_BOOKING'">
                    <div class="col-md-4 mb-2"><label class="small mb-0">Event id</label><input class="form-control form-control-sm" [(ngModel)]="link.eventId"></div>
                    <div class="col-md-4 mb-2"><label class="small mb-0">Booking / registration id</label><input class="form-control form-control-sm" [(ngModel)]="link.bookingId"></div>
                </ng-container>
                <div class="col-md-4 mb-2" *ngIf="link.purpose === 'MEMBERSHIP'"><label class="small mb-0">Member (user id)</label><input class="form-control form-control-sm" [(ngModel)]="link.userId"></div>
                <div class="col-md-4 mb-2" *ngIf="link.purpose === 'BIZ_CONFERENCE'"><label class="small mb-0">Conference entry id</label><input class="form-control form-control-sm" [(ngModel)]="link.businessConferenceId"></div>
            </div>
            <button type="button" class="btn btn-sm btn-primary mr-2" (click)="submitLink()">Link payment</button>
            <button type="button" class="btn btn-sm btn-outline-secondary" (click)="linkTarget = null">Cancel</button>
        </div>
    </div>

    <!-- ============================ PAYOUTS ============================ -->
    <div *ngIf="tab === 'payouts' && canManage" class="mt-3">
        <p class="small text-muted">Stripe pays collected money out to the bank in batches. Each payment below is matched to the payout that carried it.</p>
        <button type="button" class="btn btn-sm btn-outline-secondary mb-2" (click)="loadPayouts()" [disabled]="loading">Refresh payouts</button>
        <div class="table-responsive rana-table-flat" *ngIf="payoutsDtShow">
            <table datatable [dtOptions]="payoutsDt" class="table rana-table fin-table"></table>
        </div>
        <p *ngIf="payouts && !payouts.length" class="text-center text-muted py-4">No payouts in the last 90 days.</p>
    </div>

    <!-- ============================ ACCOUNTS ============================ -->
    <div *ngIf="tab === 'accounts' && canManage" class="mt-3">
        <p class="small text-muted">
            Each Stripe account needs one webhook so confirmations arrive instantly. In the Stripe dashboard add an endpoint at the URL shown,
            subscribe it to <em>checkout.session.*</em>, <em>charge.refunded</em>, <em>charge.dispute.*</em> and <em>payout.*</em>,
            then paste its signing secret (whsec_…) here. Without a webhook payments are still confirmed within about 15 minutes.
        </p>
        <div class="table-responsive rana-table-flat" *ngIf="accountsDtShow">
            <table datatable [dtOptions]="accountsDt" class="table rana-table fin-table"></table>
        </div>
    </div>

    <!-- Cell templates for the DataTables above (one root element each) -->
    <ng-template #exceptionActionsTpl let-e="adtData">
        <span class="text-nowrap">
            <button type="button" class="btn btn-sm btn-outline-secondary mr-1" (click)="refreshFromStripe(e.payment)">Refresh</button>
            <button type="button" *ngIf="canManage && e.category === 'UNLINKED'" class="btn btn-sm btn-outline-primary mr-1" (click)="openLink(e.payment)">Link…</button>
            <button type="button" *ngIf="canManage && e.category === 'NEEDS_REVIEW'" class="btn btn-sm btn-outline-success" (click)="markReviewed(e.payment)">Mark reviewed</button>
            <a *ngIf="stripeLink(e.payment)" class="btn btn-sm btn-link" [href]="stripeLink(e.payment)" target="_blank" rel="noopener">Stripe</a>
        </span>
    </ng-template>
    <ng-template #accountSecretTpl let-a="adtData">
        <span style="display:block;min-width:260px">
            <span *ngIf="!a.platform" class="input-group input-group-sm">
                <input type="password" class="form-control" placeholder="whsec_…" [(ngModel)]="webhookSecrets[a.ref]" autocomplete="off">
                <span class="input-group-append"><button class="btn btn-outline-primary" type="button" (click)="saveSecret(a)">Save</button></span>
            </span>
            <span *ngIf="a.platform" class="small text-muted">Set via the STRIPE_WEBHOOK_SECRET deployment setting</span>
        </span>
    </ng-template>

</ng-template>
